import _ from 'lodash';
import { useCallback, useRef } from 'react';
import { KeyCode } from '../types';
import { toggleItemWithShiftCollection } from '../utilities/collectionUtils';
import useKeyPress from './useKeyPress';

function useCheckedCollection<T extends object, U extends T | string | number>(
  items: T[],
  checkedItems: T[] | U[],
  selectKey: keyof T,
  onCheckedItemChange: (checkedItems: U[]) => void
) {
  const shiftIsPressed = useKeyPress(KeyCode.SHIFT);
  const anchor = useRef<T | null>(null);
  const isPrimitiveArray = useRef<boolean>();

  function lookup(item: U) {
    if (isPrimitiveArray.current) {
      const result = items.find((it) => it[selectKey] === (item as any));
      return result;
    } else {
      return item as T;
    }
  }

  const onCheck = useCallback(
    (checked: U, isChecked: boolean) => {
      isPrimitiveArray.current = !_.isObject(checked);
      const item = lookup(checked);

      if (!item) {
        // tslint:disable-next-line: no-console
        console.error(`Could not find ${checked} in data array.`);
        return;
      }

      const newCheckedItems = toggleItemWithShiftCollection(
        items,
        isPrimitiveArray.current
          ? items.filter((it) => checkedItems.some((c: any) => it[selectKey] === c))
          : (checkedItems as T[]),
        item,
        isChecked,
        anchor.current,
        selectKey,
        shiftIsPressed
      );

      // We need to determine the anchor
      if (newCheckedItems.length && !checkedItems.length) {
        anchor.current = item;
      } else if (newCheckedItems.length === 0) {
        anchor.current = null;
      } else {
        // There are new checked items, and there were already selected items
        // We need to check if the new item (if not shifted) is apart of the grouping or not
        if (!shiftIsPressed && isChecked) {
          const currentItemIndex = _.findIndex(
            items,
            (it) => it[selectKey] === (isPrimitiveArray.current ? checked : (checked as T)[selectKey])
          );
          const indices = new Set(
            _.map(checkedItems, (checkedItem) =>
              _.findIndex(
                items,
                (data) => data[selectKey] === (isPrimitiveArray.current ? checkedItem : (checkedItem as T)[selectKey])
              )
            )
          );

          if (
            // Need to check if it's part of previous grouping or is the first item
            !(indices.has(currentItemIndex - 1) && currentItemIndex !== 0) &&
            !(indices.has(currentItemIndex + 1) && currentItemIndex !== items.length - 1)
          ) {
            anchor.current = item;
          }
        }
      }

      if (isPrimitiveArray.current) {
        onCheckedItemChange(newCheckedItems.map((it) => it[selectKey] as any));
      } else {
        onCheckedItemChange(newCheckedItems as U[]);
      }
    },
    [items, checkedItems, selectKey, onCheckedItemChange, shiftIsPressed]
  );

  return { onCheck };
}

export default useCheckedCollection;
